/**
 * Declarative config of the contract premiums table (« Primes » tab — fiche
 * 22702): stable columns `reference` (NUM_QUI), `periodeDu` (DATDEB_QUI),
 * `periodeAu` (DTFIN_QUI) — ms → `JJ/MM/AAAA` —, `ttc` (TTC_QUI, €) and
 * `situation` (QUI_SITUATION raw value in a green « Payée » / red
 * « Impayée » badge). Rows in API order.
 */

import type { ColumnConfig, DataListConfig } from '@components/integrated/data-list';

import type { ContractPremium } from '../../api/contractsService';
import { EMPTY_STATE_TEXT } from '../shared/cardText';
import { EXTRANET_DATE_RENDERER, registerStatusBadgeRenderer } from '../shared/cellRenderers';
import { PREMIUM_SITUATION_BADGE } from '../shared/statusBadgeConfig';

/** Renderer key of the situation column (shared `PREMIUM_SITUATION_BADGE`). */
export const PREMIUM_SITUATION_RENDERER = registerStatusBadgeRenderer(
  'extranet-contractPremiumSituation',
  PREMIUM_SITUATION_BADGE,
);

/** Stable column keys. */
export type ContractPremiumsColumnKey = 'reference' | 'periodeDu' | 'periodeAu' | 'ttc' | 'situation';

/** Base columns (design order). */
export const CONTRACT_PREMIUMS_COLUMNS: Readonly<Record<ContractPremiumsColumnKey, ColumnConfig>> =
  {
    reference: {
      label: 'Référence',
      order: 1,
      type: 'text',
      className: 'font-mono text-xs',
      sortable: true,
    },
    periodeDu: {
      label: 'Période du',
      order: 2,
      type: 'custom',
      typeOptions: { renderer: EXTRANET_DATE_RENDERER },
      sortable: true,
    },
    periodeAu: {
      label: 'Période au',
      order: 3,
      type: 'custom',
      typeOptions: { renderer: EXTRANET_DATE_RENDERER },
      sortable: true,
    },
    ttc: {
      label: 'TTC',
      headerTooltip: 'Montant toutes taxes comprises',
      order: 4,
      type: 'currency',
      typeOptions: { currency: 'EUR', locale: 'fr-FR' },
      align: 'right',
      sortable: true,
    },
    situation: {
      label: 'Situation',
      order: 5,
      type: 'custom',
      typeOptions: { renderer: PREMIUM_SITUATION_RENDERER },
      align: 'right',
      sortable: true,
    },
  };

/** Base DataList config of the premiums table (inline rows, API order). */
export function buildContractPremiumsTableConfig(
  componentId: string,
  rows: readonly ContractPremium[],
): DataListConfig {
  return {
    id: componentId,
    dataSource: { data: rows as unknown as Record<string, unknown>[] },
    columns: { ...CONTRACT_PREMIUMS_COLUMNS },
    display: { size: 'compact', hoverable: false, striped: true },
    sorting: { enabled: true },
    toolbar: { visible: false },
    pagination: { mode: 'scroll', pageSize: 999_999 },
    i18n: { emptyMessage: EMPTY_STATE_TEXT },
  };
}
